import { toast } from '@repo/design-system/components/ui/toaster';
import { useCreateMapLabelMutation, useDeleteMapLabelMutation, useUpdateMapLabelMutation, useUploadLabelIconMutation } from '@repo/store/maps/api/mapsApi';
import { useAppDispatch, useAppSelector } from '@repo/store/maps/hooks';
import { selectExternalMapId, selectIsAddingMapLabel, selectIsMovingLabel, selectMovingLabelId, setIsAddingMapLabel, setIsMovingLabel } from '@repo/store/maps/slices/mapData';
import type { MapDataLabel } from '@repo/types/maps/map-types';
import type maplibregl from 'maplibre-gl';
import { useCallback, useEffect, useRef } from 'react';
import { useMapContext } from '../map-context';
import { useMapCursor } from './useMapCursor';
export function useLabelOperations() {
  const dispatch = useAppDispatch();
  const {
    map
  } = useMapContext();
  const externalId = useAppSelector(selectExternalMapId);
  const isAddingLabel = useAppSelector(selectIsAddingMapLabel);
  const isMovingLabel = useAppSelector(selectIsMovingLabel);
  const movingLabelId = useAppSelector(selectMovingLabelId);
  const {
    setCursor
  } = useMapCursor();
  const [createMapLabel, {
    isLoading: isCreating
  }] = useCreateMapLabelMutation();
  const [updateMapLabel, {
    isLoading: isUpdating
  }] = useUpdateMapLabelMutation();
  const [deleteMapLabel, {
    isLoading: isDeleting
  }] = useDeleteMapLabelMutation();
  const [uploadLabelIcon, {
    isLoading: isUploadingIcon
  }] = useUploadLabelIconMutation();

  // Store the event handler references
  const eventHandlersRef = useRef<{
    mapClick: ((e: maplibregl.MapMouseEvent) => void) | null;
    escapeHandler: ((e: KeyboardEvent) => void) | null;
    clickOutside: ((e: MouseEvent) => void) | null;
  }>({
    mapClick: null,
    escapeHandler: null,
    clickOutside: null
  });

  // Shared cleanup function to remove event listeners
  const cleanupEventListeners = useCallback(() => {
    const {
      mapClick,
      escapeHandler,
      clickOutside
    } = eventHandlersRef.current;
    if (map && mapClick) {
      map.off('click', mapClick);
      eventHandlersRef.current.mapClick = null;
    }
    if (escapeHandler) {
      document.removeEventListener('keydown', escapeHandler);
      eventHandlersRef.current.escapeHandler = null;
    }
    if (clickOutside) {
      document.removeEventListener('click', clickOutside);
      eventHandlersRef.current.clickOutside = null;
    }
    setCursor('grab');
  }, [map, setCursor]);

  // Shared function to create escape and click-outside handlers
  const createSharedHandlers = useCallback((onCancel: () => void) => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    const handleClickOutside = (e_0: MouseEvent) => {
      const target = e_0.target as Element;
      const mapContainer = map?.getContainer();
      if (mapContainer && !mapContainer.contains(target)) {
        onCancel();
      }
    };
    return {
      handleEscape,
      handleClickOutside
    };
  }, [map]);

  // Shared function to set up all event listeners
  const setupEventListeners = useCallback((mapClickHandler: (e: maplibregl.MapMouseEvent) => void, onCancel_0: () => void) => {
    if (!map) return;
    const {
      handleEscape: handleEscape_0,
      handleClickOutside: handleClickOutside_0
    } = createSharedHandlers(onCancel_0);

    // Store handlers in ref for cleanup
    eventHandlersRef.current = {
      mapClick: mapClickHandler,
      escapeHandler: handleEscape_0,
      clickOutside: handleClickOutside_0
    };

    // Add event listeners
    map.on('click', mapClickHandler);
    document.addEventListener('keydown', handleEscape_0);
    document.addEventListener('click', handleClickOutside_0);

    // Set cursor
    setCursor('crosshair');
  }, [map, createSharedHandlers, setCursor]);
  useEffect(() => {
    if (!isAddingLabel && !isMovingLabel) {
      cleanupEventListeners();
    }
  }, [isAddingLabel, isMovingLabel, cleanupEventListeners]);
  useEffect(() => {
    return cleanupEventListeners;
  }, [cleanupEventListeners]);
  const createLabelAtCoordinates = useCallback(async (lng: number, lat: number, properties?: Partial<MapDataLabel['properties']>) => {
    if (!externalId) return null;
    try {
      const newLabel = await createMapLabel({
        gameMapExternalId: externalId,
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        },
        properties: {
          title: properties?.title ?? 'New Label',
          color: properties?.color ?? '#ffffff',
          fontSize: properties?.fontSize ?? 18,
          minZoom: properties?.minZoom ?? 1,
          maxZoom: properties?.maxZoom ?? 20,
          iconId: properties?.iconId
        }
      }).unwrap();
      toast({
        title: 'Label created',
        variant: 'success'
      });
      return newLabel;
    } catch (error) {
      toast({
        title: 'Failed to create label',
        description: JSON.stringify(error),
        variant: 'error'
      });
      return null;
    }
  }, [externalId, createMapLabel]);
  const startAddingLabel = useCallback((defaultProperties?: Partial<MapDataLabel['properties']>) => {
    if (!map || !externalId) return;
    dispatch(setIsAddingMapLabel(true));
    const handleCancel = () => {
      dispatch(setIsAddingMapLabel(false));
      cleanupEventListeners();
    };
    const handleMapClick = async (e_1: maplibregl.MapMouseEvent) => {
      const {
        lng: lng_0,
        lat: lat_0
      } = e_1.lngLat;
      try {
        await createLabelAtCoordinates(lng_0, lat_0, defaultProperties);
      } finally {
        handleCancel();
      }
    };
    setupEventListeners(handleMapClick, handleCancel);
    toast({
      title: 'Click to place label',
      description: 'Press ESC to cancel',
      variant: 'info'
    });
  }, [map, externalId, dispatch, cleanupEventListeners, createLabelAtCoordinates, setupEventListeners]);
  const cancelAddingLabel = useCallback(() => {
    dispatch(setIsAddingMapLabel(false));
    cleanupEventListeners();
  }, [dispatch, cleanupEventListeners]);
  const startMovingLabel = useCallback((label: MapDataLabel) => {
    if (!map || !externalId) return;
    dispatch(setIsMovingLabel({
      isMoving: true,
      labelId: label.properties.externalId
    }));
    const handleCancel_0 = () => {
      dispatch(setIsMovingLabel({
        isMoving: false,
        labelId: null
      }));
      cleanupEventListeners();
    };
    const handleMapClick_0 = async (e_2: maplibregl.MapMouseEvent) => {
      const {
        lng: lng_1,
        lat: lat_1
      } = e_2.lngLat;
      try {
        await updateMapLabel({
          externalId: label.properties.externalId,
          request: {
            geometry: {
              type: 'Point',
              coordinates: [lng_1, lat_1]
            },
            properties: label.properties
          },
          gameMapExternalId: externalId
        }).unwrap();
        toast({
          title: 'Label moved',
          variant: 'success'
        });
      } catch (error_0) {
        toast({
          title: 'Failed to move label',
          description: JSON.stringify(error_0),
          variant: 'error'
        });
      } finally {
        handleCancel_0();
      }
    };
    setupEventListeners(handleMapClick_0, handleCancel_0);
    toast({
      title: 'Click to move label',
      description: 'Press ESC to cancel',
      variant: 'info'
    });
  }, [map, externalId, dispatch, cleanupEventListeners, updateMapLabel, setupEventListeners]);
  const cancelMovingLabel = useCallback(() => {
    dispatch(setIsMovingLabel({
      isMoving: false,
      labelId: null
    }));
    cleanupEventListeners();
  }, [dispatch, cleanupEventListeners]);
  const updateLabelProperties = useCallback(async (labelId: string, properties_0: {
    title?: string;
    color?: string;
    fontSize?: number;
    minZoom?: number;
    maxZoom?: number;
    iconId?: number;
  }, currentGeometry?: {
    type: 'Point';
    coordinates: [number, number];
  }) => {
    if (!externalId) return;
    try {
      // Ensure all required fields are present
      const completeProperties = {
        title: properties_0.title ?? 'Unnamed Label',
        color: properties_0.color ?? '#ffffff',
        fontSize: properties_0.fontSize ?? 18,
        minZoom: properties_0.minZoom ?? 1,
        maxZoom: properties_0.maxZoom ?? 20,
        ...(properties_0.iconId !== undefined && {
          iconId: properties_0.iconId
        })
      };
      const label_0 = await updateMapLabel({
        externalId: labelId,
        request: {
          properties: completeProperties,
          geometry: currentGeometry || {
            type: 'Point',
            coordinates: [0, 0]
          } // Use provided geometry
        },
        gameMapExternalId: externalId
      }).unwrap();
      toast({
        title: 'Label updated',
        variant: 'success'
      });
      return label_0;
    } catch (error_1) {
      toast({
        title: 'Failed to update label',
        description: JSON.stringify(error_1),
        variant: 'error'
      });
    }
  }, [externalId, updateMapLabel]);
  const deleteLabelById = useCallback(async (labelId_0: string) => {
    if (!externalId) return;
    try {
      await deleteMapLabel({
        externalId: labelId_0,
        gameMapExternalId: externalId
      }).unwrap();
      toast({
        title: 'Label deleted',
        variant: 'success'
      });
    } catch (error_2) {
      toast({
        title: 'Failed to delete label',
        description: JSON.stringify(error_2),
        variant: 'error'
      });
    }
  }, [externalId, deleteMapLabel]);
  const uploadIconForLabel = useCallback(async (labelId_1: string, iconFile: File) => {
    if (!externalId) return;
    try {
      const result = await uploadLabelIcon({
        externalId: labelId_1,
        icon: iconFile,
        gameMapExternalId: externalId
      }).unwrap();
      toast({
        title: 'Icon uploaded',
        variant: 'success'
      });
      return result.icon_id;
    } catch (error_3) {
      toast({
        title: 'Failed to upload icon',
        description: JSON.stringify(error_3),
        variant: 'error'
      });
    }
  }, [externalId, uploadLabelIcon]);
  return {
    isAddingLabel,
    isMovingLabel,
    movingLabelId,
    startAddingLabel,
    cancelAddingLabel,
    startMovingLabel,
    cancelMovingLabel,
    createLabelAtCoordinates,
    updateLabelProperties,
    deleteLabelById,
    uploadIconForLabel,
    isCreating,
    isUpdating,
    isDeleting,
    isUploadingIcon
  };
}